import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import AdminSidebar from './components/AdminSidebar';
import StatsCard from './components/StatsCard';

interface DashboardStats {
  projects: number;
  activeProjects: number;
  volunteers: number;
  pendingApplications: number;
  messages: number;
  unreadMessages: number;
  users: number;
}

export default function AdminDashboardPage() {
  const { profile } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    projects: 0,
    activeProjects: 0,
    volunteers: 0,
    pendingApplications: 0,
    messages: 0,
    unreadMessages: 0,
    users: 0,
  });
  const [recentMessages, setRecentMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const [
        { count: projects },
        { count: activeProjects },
        { count: volunteers },
        { count: pendingApps },
        { count: messages },
        { count: unreadMsgs },
        { count: users },
        { data: msgs },
      ] = await Promise.all([
        supabase.from('projects').select('*', { count: 'exact', head: true }),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('volunteer_opportunities').select('*', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('volunteer_applications').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('contact_messages').select('*', { count: 'exact', head: true }),
        supabase.from('contact_messages').select('*', { count: 'exact', head: true }).eq('status', 'unread'),
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('contact_messages').select('id, name, email, subject, created_at, status').order('created_at', { ascending: false }).limit(5),
      ]);

      setStats({
        projects: projects ?? 0,
        activeProjects: activeProjects ?? 0,
        volunteers: volunteers ?? 0,
        pendingApplications: pendingApps ?? 0,
        messages: messages ?? 0,
        unreadMessages: unreadMsgs ?? 0,
        users: users ?? 0,
      });
      setRecentMessages(msgs ?? []);
      setLoading(false);
    };

    fetchStats();
  }, []);

  const quickActions = [
    { label: 'Add Project', icon: 'ri-add-circle-line', path: '/admin/projects', color: 'bg-[#3B9EF5]/10 text-[#3B9EF5]' },
    { label: 'View Messages', icon: 'ri-mail-open-line', path: '/admin/messages', color: 'bg-amber-50 text-amber-600' },
    { label: 'Manage Team', icon: 'ri-user-settings-line', path: '/admin/users', color: 'bg-rose-50 text-rose-500' },
    { label: 'Manage Volunteers', icon: 'ri-team-line', path: '/admin/volunteers', color: 'bg-green-50 text-green-600' },
    { label: 'Edit Content', icon: 'ri-translate-2', path: '/admin/content', color: 'bg-indigo-50 text-indigo-500' },
    { label: 'Photo Slider', icon: 'ri-image-line', path: '/admin/photos', color: 'bg-violet-50 text-violet-500' },
  ];

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />

      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#1B2838]">
            Welcome back, {profile?.name ?? 'Admin'} 👋
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Here's what's happening with CAF today.
          </p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                <div className="w-10 h-10 bg-gray-100 rounded-lg mb-3"></div>
                <div className="h-7 bg-gray-100 rounded w-16 mb-1"></div>
                <div className="h-4 bg-gray-100 rounded w-24"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatsCard
              label="Total Projects"
              value={stats.projects}
              icon="ri-folder-line"
              color="bg-[#3B9EF5]/10 text-[#3B9EF5]"
              sub={`${stats.activeProjects} active`}
            />
            <StatsCard
              label="Volunteer Roles"
              value={stats.volunteers}
              icon="ri-team-line"
              color="bg-green-50 text-green-600"
              sub={`${stats.pendingApplications} pending apps`}
            />
            <StatsCard
              label="Contact Messages"
              value={stats.messages}
              icon="ri-mail-line"
              color="bg-amber-50 text-amber-600"
              sub={`${stats.unreadMessages} unread`}
            />
            <Link to="/admin/users" className="block">
              <StatsCard
                label="Team Members"
                value={stats.users}
                icon="ri-user-settings-line"
                color="bg-rose-50 text-rose-500"
                sub="Admins & Editors"
              />
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-base font-bold text-[#1B2838] mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {quickActions.map((action) => (
                <Link
                  key={action.path}
                  to={action.path}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${action.color}`}>
                    <i className={action.icon}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#1B2838]">
                    {action.label}
                  </span>
                  <i className="ri-arrow-right-line text-gray-300 ml-auto group-hover:text-gray-500 transition-colors"></i>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Messages */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[#1B2838]">Recent Messages</h2>
              <Link to="/admin/messages" className="text-xs text-[#3B9EF5] hover:underline cursor-pointer">
                View all
              </Link>
            </div>
            {recentMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-mail-line text-2xl"></i>
                </div>
                <p className="text-sm">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentMessages.map((msg) => (
                  <div key={msg.id} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1B2838] text-white text-xs font-bold flex-shrink-0">
                      {msg.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1B2838] truncate">{msg.name}</span>
                        {msg.status === 'unread' && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#3B9EF5] flex-shrink-0"></span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{msg.subject ?? msg.email}</div>
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap">
                      {new Date(msg.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Phase Status */}
        <div className="mt-6 bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-base font-bold text-[#1B2838] mb-4">Project Phases</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { phase: 'Phase 1', label: 'Public Website UI', status: 'done' },
              { phase: 'Phase 2', label: 'Multilingual (EN/FR/DE)', status: 'done' },
              { phase: 'Phase 3', label: 'Supabase Backend', status: 'done' },
              { phase: 'Phase 4', label: 'Admin CMS Panel', status: 'done' },
              { phase: 'Phase 5', label: 'Pashto & Dari CMS', status: 'done' },
              { phase: 'Phase 6', label: 'Dynamic Data', status: 'done' },
              { phase: 'Phase 7', label: 'Polish & Launch', status: 'active' },
            ].map((p) => (
              <div
                key={p.phase}
                className={`rounded-lg px-4 py-3 border ${
                  p.status === 'done'
                    ? 'bg-[#3B9EF5]/10 border-[#3B9EF5]/30'
                    : p.status === 'active'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-gray-50 border-gray-100'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <i
                    className={`text-xs ${
                      p.status === 'done'
                        ? 'ri-checkbox-circle-fill text-[#3B9EF5]'
                        : p.status === 'active'
                        ? 'ri-loader-4-line text-amber-500'
                        : 'ri-time-line text-gray-400'
                    }`}
                  ></i>
                  <span className="text-xs font-bold text-gray-500">{p.phase}</span>
                </div>
                <div className="text-xs text-[#1B2838] font-medium">{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
